import React from 'react'
import cn from 'classnames'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import { throttle } from 'lodash'

import styles from './infiniteList.scss'

type IListItem = ({
  index,
  style,
  data,
}: {
  index: number
  style: React.CSSProperties
  data: any
}) => React.ReactElement

interface IProps {
  className?: string
  outerElementRef?: React.RefObject<HTMLDivElement>
  innerElementRef?: React.RefObject<HTMLDivElement>
  height: number
  width: number
  itemCount: number
  itemSize: number
  itemData: any
  itemRenderer:
    | React.ComponentClass<ListChildComponentProps>
    | React.SFC<ListChildComponentProps>
  stickyIndices: number[]
  stickyItemsClassName: string
  overscan: number
  itemKey: (idx: number) => string
  onLoad?: () => void
}

interface IInfiniteListContext {
  stickyIndices: number[]
  stickyItemsClassName: string
  itemSize: IProps['itemSize']
  itemData: IProps['itemData']
  itemKey: IProps['itemKey']
  itemRenderer: IProps['itemRenderer']
}
const InfiniteListContext: React.Context<IInfiniteListContext> = React.createContext(
  {} as IInfiniteListContext
)

class InfiniteList extends React.PureComponent<IProps> {
  public static defaultProps = {
    stickyIndices: [],
    stickyItemsClassName: '',
    overscan: 10,
  }

  list: React.RefObject<any> = React.createRef()
  lastScrollTop: number = 0 // Used to detemine which direction scrolling happens in (horizontal vs vertical)
  isLoading: boolean = false /* This exists to throttle loading */

  handleHorizontalScrollThrottled: () => void

  constructor(props: IProps) {
    super(props)

    this.handleHorizontalScrollThrottled = throttle(
      this.handleHorizontalScroll,
      100,
      {
        leading: true,
        trailing: true,
      }
    )
  }

  componentDidUpdate() {
    this.isLoading = false
  }

  handleScroll = () => {
    const container = this.list.current._outerRef

    if (container.scrollTop === this.lastScrollTop) {
      // Only scrolled horizontally, no need to proceed, load more will never happen
      return
    }

    this.lastScrollTop = container.scrollTop
    this.handleHorizontalScrollThrottled()
  }

  handleHorizontalScroll = () => {
    const { itemSize, onLoad } = this.props

    if (this.isLoading || !onLoad) {
      return
    }

    const container = this.list.current._outerRef
    const totalHeight = container.scrollHeight
    const scrollBottom = container.scrollTop + container.clientHeight

    if (totalHeight - scrollBottom < 15 * itemSize) {
      // ~15 records from the bottom
      this.isLoading = true
      onLoad()
    }
  }

  render() {
    const {
      className,
      outerElementRef,
      innerElementRef,
      height,
      width,
      itemCount,
      itemKey,
      itemSize,
      itemData,
      itemRenderer,
      stickyIndices,
      stickyItemsClassName,
      overscan,
    } = this.props

    return (
      <InfiniteListContext.Provider
        value={{
          stickyIndices,
          stickyItemsClassName,
          itemSize,
          itemData,
          itemKey,
          itemRenderer,
        }}
      >
        <div
          data-cy="infinite-list"
          className={className}
          onScroll={this.handleScroll}
        >
          <FixedSizeList
            ref={this.list}
            outerRef={outerElementRef}
            innerRef={innerElementRef}
            height={height}
            width={width}
            itemCount={itemCount}
            itemSize={itemSize}
            itemKey={itemKey}
            itemData={itemData}
            overscanCount={overscan}
            outerElementType={outerElementType}
            innerElementType={innerElementType}
          >
            {itemRenderer}
          </FixedSizeList>
        </div>
      </InfiniteListContext.Provider>
    )
  }
}

/**
 * The inner element to use in react-window's FixedSizeList
 * We're overriding this to render a few sticky items before all other, regular items
 */
const innerElementType = React.forwardRef((
  { children, ...rest }: { children: IListItem[] },
  ref: React.Ref<HTMLDivElement> // This is really the `innerElementRef` prop
) => (
  <InfiniteListContext.Consumer>
    {({
      stickyItemsClassName,
      stickyIndices = [],
      itemSize,
      itemData,
      itemKey,
      itemRenderer,
    }) => (
      <div ref={ref} {...rest}>
        <div className={cn(styles.stickyItems, stickyItemsClassName)}>
          {stickyIndices.map(idx =>
            React.createElement(itemRenderer, {
              key: itemKey(idx),
              index: idx,
              style: {
                display: 'flex',
                position: 'absolute',
                top: idx * itemSize,
                left: 0,
                width: '100%',
                height: itemSize,
              },
              data: { ...itemData, sticky: true },
            })
          )}
        </div>

        {React.Children.map(children, (child, idx) =>
          stickyIndices.includes(idx) ? null : child
        )}
      </div>
    )}
  </InfiniteListContext.Consumer>
))

/**
 * The outer element to use in react-window's FixedSizeList.
 * We're overriding this so that we can attach a ref to it. This is needed for:
 * 1. shortcuts to be able to target this DOM element
 * 2. Being able to scroll this list to a point during scrollIntoView
 */
const outerElementType = React.forwardRef((
  { children, className, ...rest }: { children: any; className: string },
  ref: React.Ref<HTMLDivElement> // This is really the `outerElementRef` prop
) => (
  <div
    ref={ref}
    tabIndex={1} // Need a tabindex so it can be focused
    className={cn(styles.outer, className)}
    {...rest}
  >
    {children}
  </div>
))

export default InfiniteList
